import React, { useMemo } from 'react';

const CELL_SIZE = 12;
const CELL_GAP = 2;
const TOTAL = CELL_SIZE + CELL_GAP;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

const getColor = (count) => {
  if (count === 0) return '#f3f4f6';
  if (count <= 1) return '#d1fae5';
  if (count <= 3) return '#6ee7b7';
  if (count <= 5) return '#34d399';
  return '#059669';
};

const ActivityHeatmap = ({ heatmapData = [], dayOfWeekDistribution = [] }) => {
  const { grid, monthLabels, totalActivities } = useMemo(() => {
    const safeData = Array.isArray(heatmapData) ? heatmapData : [];
    if (safeData.length === 0) {
      return { grid: [], monthLabels: [], totalActivities: 0 };
    }
    const cells = [];
    let total = 0;

    const firstDate = new Date(safeData[0]?.date);
    const startDay = Number.isNaN(firstDate.getTime()) ? 0 : firstDate.getDay();

    for (let i = 0; i < startDay; i += 1) {
      cells.push({ date: '', count: -1 });
    }

    safeData.forEach(d => {
      cells.push(d);
      total += d.count || 0;
    });

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }

    const mLabels = [];
    let lastMonth = -1;
    weeks.forEach((week, wi) => {
      const validCell = week.find(c => c.date);
      if (validCell) {
        const m = new Date(validCell.date).getMonth();
        if (m !== lastMonth) {
          mLabels.push({ weekIdx: wi, label: MONTHS[m] });
          lastMonth = m;
        }
      }
    });

    return { grid: weeks, monthLabels: mLabels, totalActivities: total };
  }, [heatmapData]);

  const safeDow = Array.isArray(dayOfWeekDistribution) ? dayOfWeekDistribution : [];
  const maxPct = safeDow.length > 0 ? Math.max(...safeDow.map(x => x.pct || 0)) : 0;

  const svgWidth = grid.length * TOTAL + 30;
  const svgHeight = 7 * TOTAL + 24;

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-green-500">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Activity Heatmap</h3>
          <p className="text-xs text-gray-500">{totalActivities} actions in the past year</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <span>Less</span>
          {[0, 1, 2, 4, 6].map((v, i) => (
            <span key={i} className="inline-block rounded-sm" style={{ width: 10, height: 10, backgroundColor: getColor(v) }} />
          ))}
          <span>More</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 min-w-0">
          {grid.length > 0 ? (
            <div className="overflow-x-auto pb-2">
              <svg width={svgWidth} height={svgHeight} className="block">
                {DAYS.map((label, i) => (
                  <text key={i} x={0} y={24 + i * TOTAL + CELL_SIZE / 2} fontSize={9} fill="#9ca3af" dominantBaseline="middle">
                    {label}
                  </text>
                ))}
                {monthLabels.map((m, i) => (
                  <text key={i} x={30 + m.weekIdx * TOTAL} y={12} fontSize={9} fill="#9ca3af">
                    {m.label}
                  </text>
                ))}
                {grid.map((week, wi) =>
                  week.map((cell, di) => {
                    if (cell.count < 0) return null;
                    return (
                      <rect
                        key={`${wi}-${di}`}
                        x={30 + wi * TOTAL}
                        y={20 + di * TOTAL}
                        width={CELL_SIZE}
                        height={CELL_SIZE}
                        rx={2}
                        fill={getColor(cell.count)}
                      >
                        <title>{cell.date}: {cell.count} actions</title>
                      </rect>
                    );
                  })
                )}
              </svg>
            </div>
          ) : (
            <div className="py-8 text-center text-xs text-gray-400">
              Activity will appear here as you save jobs, polish resumes, and apply.
            </div>
          )}
        </div>

        <div className="lg:pl-4 lg:border-l lg:border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">Most productive days</p>
          <div className="space-y-1.5">
            {safeDow.map((d) => {
              const isMax = (d.pct || 0) > 0 && d.pct === maxPct;
              return (
                <div key={d.day} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-7">{d.day}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(d.pct || 0) * 100}%`,
                        backgroundColor: isMax ? '#059669' : '#6ee7b7',
                      }}
                    />
                  </div>
                  <span className={`text-xs w-7 text-right ${isMax ? 'font-bold text-green-700' : 'text-gray-400'}`}>
                    {d.label}
                  </span>
                  {isMax && <span className="text-[10px] text-green-600">peak</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap;
